import { env } from '@/config/env'

/**
 * Ending the officer's session, from the screens that have to do it.
 *
 * A thin layer over lib/auth/officerSso.ts, and it exists for three reasons.
 *
 * It owns the `env.officerSso` guard, so no caller has to remember it. It owns the
 * dynamic import, so MSAL's ~200KB `vendor-msal` chunk stays out of the receipt and
 * the header button — the same reason OfficerLoginPage.tsx loads the module this way.
 * And it swallows failures: every caller is a citizen leaving a finished or abandoned
 * application, and a sign-out that throws must never be what stops them.
 *
 * It lives in lib/ rather than in the officer-login slice because ResetApplicationButton
 * is in application-session, and the boundaries rule (ADR 0003) forbids one feature
 * reaching into another. Every slice may import from lib.
 */
const loadSso = () => import('@/lib/auth/officerSso')

/**
 * Drop the officer's tokens from this browser. Silent — nothing navigates.
 *
 * This is what makes the NEXT application ask for a sign-in: with the MSAL cache
 * empty, the Officer Login screen finds no account, so it offers "Sign in with SSO"
 * and a real redirect rather than "Signed in as …" and a Continue button that would
 * record the application under whoever used this workstation last.
 *
 * Safe to call while a screen the citizen is still reading is mounted, which is the
 * whole point: it runs on finalise success, with the receipt on screen.
 */
export async function endOfficerSessionQuietly(): Promise<void> {
  if (!env.officerSso) return

  try {
    const { clearOfficerSession } = await loadSso()
    await clearOfficerSession()
  } catch (cause) {
    if (!env.isProduction)
      console.error('[officer-sso] Could not clear the officer session.', cause)
  }
}

/**
 * End the officer's session at Entra as well as here, by navigating to Microsoft.
 *
 * ⚠ Takes the whole page with it, so call it only where the app is already leaving.
 *
 * Resolves `false` when nothing was started — SSO is off, or the redirect could not
 * be begun — so the caller can rely on its own `navigate()` instead. It cannot
 * resolve `true` in the success case, because the browser is gone by then; a promise
 * that settles at all is the failure signal, exactly as it is for the sign-in.
 */
export async function signOutOfficerAndLeave(): Promise<boolean> {
  if (!env.officerSso) return false

  try {
    const { signOutOfficer } = await loadSso()
    await signOutOfficer()
    return true
  } catch (cause) {
    if (!env.isProduction) console.error('[officer-sso] Could not sign the officer out.', cause)
    return false
  }
}
